<app-dialog
  #dialog
  [key]="dialogKey"
  [action]="'bank-limit-proposal'"
  [headerTheme]="'simple'"
  [title]="
    proposal() ? 'Limites informados pelo ' + proposal()!.bankName : 'Limites informados pelo banco'
  "
  [appendTo]="'body'"
  [maxWidth]="'980px'">
  <div class="limit-proposal">
    @if (loading() && !proposal()) {
      <div class="limit-proposal__state" role="status">
        <app-cog-loading-spinner />
        <span>Abrindo a resposta do banco...</span>
        <span class="limit-proposal__skeleton"></span>
        <span class="limit-proposal__skeleton limit-proposal__skeleton--short"></span>
      </div>
    } @else if (loadFailed()) {
      <div class="limit-proposal__state limit-proposal__state--error" role="alert">
        <i class="pi pi-exclamation-triangle"></i>
        <span>Não foi possível abrir a resposta do banco agora. Tente de novo.</span>
        <app-button
          label="Tentar de novo"
          theme="white"
          size="small"
          [noMargin]="true"
          (sendClick)="load()"></app-button>
      </div>
    } @else if (proposal(); as data) {
      <div class="limit-proposal__head">
        <p class="limit-proposal__subtitle">{{ subtitle() }}</p>
        <app-button
          label="Ver e-mail completo"
          icon="pi pi-envelope"
          theme="white"
          size="small"
          [noMargin]="true"
          [isLoading]="openingEmail()"
          loadingText="Abrindo..."
          (sendClick)="openEmail()"></app-button>
      </div>

      @if (showRejectReason()) {
        <div class="limit-proposal__reject" #rejectPanel>
          <p class="limit-proposal__reject-title">Rejeitar a resposta do {{ data.bankName }}</p>
          <app-input
            type="textarea"
            label="Motivo da rejeição (opcional)"
            placeholder="Ex.: o banco mandou o limite disponível, não o total"
            [formControl]="rejectReason"
            [maxLength]="500"
            [textCenter]="false" />
          <p class="limit-proposal__muted">{{ rejectHint }}</p>
        </div>
      }

      @if (decision(); as decisionLine) {
        <div
          class="limit-proposal__banner"
          [class.limit-proposal__banner--ok]="data.status === 'Confirmed'"
          role="status">
          <i
            class="pi"
            [class.pi-check-circle]="data.status === 'Confirmed'"
            [class.pi-times-circle]="data.status === 'Rejected'"></i>
          <span>{{ decisionLine }}</span>
        </div>
      }

      @if (senderWarning(); as warning) {
        <div class="limit-proposal__banner limit-proposal__banner--warning" role="alert">
          <i class="pi pi-shield"></i>
          <span>{{ warning }}</span>
        </div>
      }

      @if (data.extractionError) {
        <div class="limit-proposal__banner limit-proposal__banner--warning" role="alert">
          <i class="pi pi-exclamation-triangle"></i>
          <span>{{ data.extractionError }}</span>
        </div>
      } @else if (!data.isLimitAnswer && isPending()) {
        <div class="limit-proposal__banner limit-proposal__banner--warning" role="status">
          <i class="pi pi-info-circle"></i>
          <span
            >O banco respondeu sem informar limites (ex.: "vou verificar"). Rejeite esta resposta e
            aguarde a próxima, ou preencha os valores se você já os tem.</span
          >
        </div>
      }

      @if (topError(); as error) {
        <div class="limit-proposal__banner limit-proposal__banner--error" role="alert">
          <i class="pi pi-times-circle"></i>
          <span>{{ error }}</span>
        </div>
      }

      @if (data.globalNotes) {
        <p class="limit-proposal__notes"><b>Observação do banco:</b> {{ data.globalNotes }}</p>
      }

      @for (view of lineViews(); track view.line.limitTypeCode) {
        <section class="limit-proposal__line" [attr.aria-label]="view.title">
          <div class="limit-proposal__line-head">
            <h3 class="limit-proposal__line-title">{{ view.title }}</h3>
            @if (isPending()) {
              <app-switch
                label="Atualizar"
                [ariaLabel]="'Atualizar o limite de ' + view.title"
                [disabled]="showRejectReason()"
                [checked]="lineGroup(view.index).controls.apply.value"
                (sendChecked)="setApply(view.index, $event)"></app-switch>
            }
          </div>
          @if (changeViews()[view.index]; as change) {
            <div class="limit-proposal__change limit-proposal__change--{{ change.direction }}">
              <div class="limit-proposal__change-side">
                <span class="limit-proposal__change-label">Hoje</span>
                <span class="limit-proposal__change-value">{{ change.currentText }}</span>
              </div>
              <i class="pi pi-arrow-right limit-proposal__change-arrow" aria-hidden="true"></i>
              <div class="limit-proposal__change-side">
                <span class="limit-proposal__change-label">{{
                  isPending() ? 'Novo' : 'Gravado'
                }}</span>
                <span class="limit-proposal__change-value limit-proposal__change-value--new">{{
                  change.newText
                }}</span>
              </div>
              @if (change.directionLabel) {
                <span class="limit-proposal__change-tag">{{ change.directionLabel }}</span>
              }
            </div>
          }
          <div class="limit-proposal__columns">
            <div class="limit-proposal__form">
              @if (isPending()) {
                @let group = lineGroup(view.index);
                <app-input
                  type="number"
                  prefix="R$ "
                  label="Valor em reais"
                  [formGroup]="group"
                  controlName="amountBrl"
                  [id]="'limit-proposal-amount-' + view.index"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="2"
                  [disabled]="!group.controls.apply.value || showRejectReason()"
                  [required]="group.controls.apply.value" />
                @if (view.foreignAmount) {
                  <p class="limit-proposal__muted">{{ view.foreignAmount }}</p>
                }
                <div class="limit-proposal__dates">
                  <app-calendar
                    [formGroup]="group"
                    controlName="validityStartDate"
                    [inputId]="'limit-proposal-start-' + view.index"
                    label="Início da vigência"
                    placeholder="Início da vigência"
                    [disabled]="!group.controls.apply.value || showRejectReason()" />
                  <app-calendar
                    [formGroup]="group"
                    controlName="validityEndDate"
                    [inputId]="'limit-proposal-end-' + view.index"
                    label="Fim da vigência"
                    placeholder="Fim da vigência"
                    [disabled]="!group.controls.apply.value || showRejectReason()" />
                </div>
                @if (view.line.validityText) {
                  <p class="limit-proposal__muted">
                    Vigência no e-mail: {{ view.line.validityText }}
                  </p>
                }
              } @else {
                <p class="limit-proposal__applied">
                  {{ view.applied ?? 'Não atualizado nesta resposta.' }}
                </p>
              }
            </div>

            <div class="limit-proposal__excerpt">
              <p class="limit-proposal__excerpt-label">Trecho do e-mail</p>
              @if (view.excerpt.text) {
                <blockquote class="limit-proposal__quote">{{ view.excerpt.text }}</blockquote>
                @if (view.excerpt.lowConfidence) {
                  <p class="limit-proposal__low-confidence">
                    <i class="pi pi-exclamation-triangle"></i>{{ lowConfidenceText }}
                  </p>
                }
              } @else {
                <p class="limit-proposal__muted">{{ view.excerpt.emptyText }}</p>
              }
              @if (view.line.notes) {
                <p class="limit-proposal__muted">{{ view.line.notes }}</p>
              }
            </div>
          </div>
        </section>
      }

      @if (isPending() && aboveGlobalAmount() !== null) {
        <div class="limit-proposal__global">
          <app-switch
            [label]="raiseGlobalLabel()"
            [checked]="raiseGlobalLimit.value"
            (sendChecked)="raiseGlobalLimit.setValue($event)"></app-switch>
          @if (globalWarning(); as warning) {
            <p class="limit-proposal__global-warning">{{ warning }}</p>
          }
        </div>
      }

      @if (isPending() && validationErrors().length > 0 && !globalWarning()) {
        <p class="limit-proposal__todo">
          <i class="pi pi-info-circle"></i>Para confirmar: {{ validationErrors()[0] }}
        </p>
      }
    }
  </div>

  <ng-template #footer>
    <div class="dialog__footer limit-proposal__footer">
      @if (isPending()) {
        @if (showRejectReason()) {
          <app-button
            label="Voltar"
            theme="outline-grey"
            [noMargin]="true"
            (sendClick)="cancelReject()"></app-button>
          <app-button
            label="Rejeitar resposta"
            icon="pi pi-times"
            theme="danger"
            [noMargin]="true"
            [isLoading]="rejecting()"
            loadingText="Rejeitando..."
            (sendClick)="confirmReject()"></app-button>
        } @else {
          <app-button
            label="Rejeitar"
            theme="outline-grey"
            [noMargin]="true"
            [disabled]="submitting()"
            (sendClick)="startReject()"></app-button>
          <app-button
            label="Confirmar e atualizar limites"
            icon="pi pi-check"
            [noMargin]="true"
            [disabled]="!isFormValid()"
            [isLoading]="submitting()"
            loadingText="Gravando..."
            (sendClick)="submitAction()"></app-button>
        }
      } @else {
        <app-button
          label="Fechar"
          theme="outline-grey"
          [noMargin]="true"
          (sendClick)="close()"></app-button>
      }
    </div>
  </ng-template>
</app-dialog>

<app-modal-rfq-emails #modalEmails />
